/**
 * The scrub-and-upload orchestrator — the layer that enforces the whole security
 * invariant: **raw PII never leaves the browser.**
 *
 * Given a `File`, a user-confirmed `ScrubPlan`, an imported tenant `CryptoKey`,
 * and an `UploadTransport`, it streams the file row by row and constructs the
 * scrubbed CSV byte stream cell-by-cell:
 *
 *   - **dropped PII columns** (`plan.droppedColumnIndexes`) are NEVER appended;
 *   - the **Loan ID** column is appended ONLY as `computeProtectedId(...)` (a
 *     one-way HMAC), never raw;
 *   - the **Tax ID** column, when selected, is appended ONLY as its protected ID;
 *   - every **other kept column** is appended unchanged (PRODUCT.md: "All
 *     remaining columns are uploaded unchanged").
 *
 * There is exactly one code path that appends to the part buffer
 * (`scrubRowCells` → `encodeCsvRow`) and the transport only ever uploads that
 * buffer, so raw Loan/Tax IDs and dropped columns are *structurally* incapable of
 * reaching the transport. The PII-safety property test drives this with a fake
 * transport that captures every byte and proves no raw value escapes.
 *
 * Streaming + bounded memory: rows flow through in batches; each scrubbed batch is
 * appended to the uniform {@link PartChunker}, which slices parts at EXACTLY
 * `PART_SIZE_BYTES` decoupled from row boundaries (R2 rejects `complete()` when
 * non-final parts differ in size — F3). Part uploads are **double-buffered**: the next part is
 * scrubbed while the previous one is in flight (at most ONE upload in flight, so
 * multipart part numbers stay strictly sequential), so scrub CPU overlaps upload
 * I/O instead of serializing with it and the working set is ≈ two parts + one
 * batch, independent of total file size. HMAC calls are batched (`Promise.all`
 * per batch) to keep WebCrypto pipelined without unbounded outstanding promises.
 *
 * Browser-only: depends on WebCrypto (via `computeProtectedId`), the streaming
 * parser, the transport, and the isomorphic CSV/column helpers from /common.
 */

import {
  PART_SIZE_BYTES,
  encodeCsvRow,
  scrubbedColumnIndexes,
  scrubbedHeaderRow,
  scrubbedNameForIndex,
} from '../../common/uploader';
import type { ScrubPlan } from '../../common/uploader';
import { openRowStream } from './parse-stream';
import type { ParsedRow } from './parse-stream';
import { PartChunker } from './part-chunker';
import { computeProtectedId } from './protected-id-crypto';
import type { UploadTransport, UploadCompleteResult } from './upload-transport';
import { HeaderDriftError } from './errors';

/** How many rows to scrub per batch — bounds outstanding HMAC promises. */
const ROW_BATCH_SIZE = 1000;

/** Progress emitted as the pipeline runs. */
export interface ScrubProgress {
  readonly rowsProcessed: number;
  readonly bytesUploaded: number;
}

/** The completed scrub result, including the transport's object location. */
export interface ScrubResult extends UploadCompleteResult {
  readonly rowsProcessed: number;
  readonly bytesUploaded: number;
}

/** Inputs to {@link runScrubAndUpload}. */
export interface ScrubAndUploadArgs {
  readonly file: File;
  readonly plan: ScrubPlan;
  readonly cryptoKey: CryptoKey;
  readonly transport: UploadTransport;
  /** The shared upload-EVENT id this file attaches to (threaded to transfer-init). */
  readonly uploadId: string;
  readonly onProgress?: (progress: ScrubProgress) => void;
  /** Aborts the pipeline AND the transport (R2 multipart abort) when fired. */
  readonly signal?: AbortSignal;
}

const ENCODER = new TextEncoder();

/**
 * Verify the live header set matches the plan's by index + name. A mismatch means
 * the file changed between preflight and scrub (the user edited or re-selected
 * it); we abort rather than upload a mis-scrubbed file.
 */
function assertHeadersMatch(
  liveHeaders: readonly { readonly index: number; readonly name: string }[],
  plan: ScrubPlan,
): void {
  if (liveHeaders.length !== plan.headers.length) {
    throw new HeaderDriftError(
      'File header count changed since preflight ' +
        `(expected ${plan.headers.length}, got ${liveHeaders.length}). Please re-run preflight.`,
    );
  }
  for (const planned of plan.headers) {
    const live = liveHeaders[planned.index];
    if (live?.name !== planned.name) {
      throw new HeaderDriftError(
        `File header at column ${planned.index} changed since preflight ` +
          `(expected ${JSON.stringify(planned.name)}, got ${JSON.stringify(live?.name)}). ` +
          'Please re-run preflight.',
      );
    }
  }
}

/**
 * Build one scrubbed output row's cells, in scrubbed-column order: skip dropped
 * columns, substitute the protected ID for Loan/Tax ID, copy everything else
 * unchanged. The HMAC awaits are the only async work, so the whole row resolves
 * with a single `await` of the substituted cells.
 *
 * This is the ONLY place raw cells are read into output; it never emits a raw
 * Loan/Tax value and never reads a dropped column.
 */
async function scrubRowCells(
  row: ParsedRow,
  plan: ScrubPlan,
  keptIndexes: readonly number[],
  cryptoKey: CryptoKey,
): Promise<string[]> {
  return Promise.all(
    keptIndexes.map(async (index) => {
      const raw = row.cells[index] ?? '';
      if (index === plan.loanIdColumnIndex) {
        return computeProtectedId(cryptoKey, raw, 'loan');
      }
      if (plan.taxIdColumnIndex !== null && index === plan.taxIdColumnIndex) {
        return computeProtectedId(cryptoKey, raw, 'tax');
      }
      return raw;
    }),
  );
}

/**
 * Stream the file, scrub each row, and push the scrubbed CSV through the
 * transport in `PART_SIZE_BYTES` parts. Aborts the transport on any error,
 * header drift, or `signal` abort.
 */
export async function runScrubAndUpload(args: ScrubAndUploadArgs): Promise<ScrubResult> {
  const { file, plan, cryptoKey, transport, uploadId, onProgress, signal } = args;

  const throwIfAborted = (): void => {
    if (signal?.aborted) {
      throw new DOMException('Scrub aborted', 'AbortError');
    }
  };

  let rowsProcessed = 0;
  let bytesUploaded = 0;
  let began = false;

  // The uniform-part chunker (P2 — F3): scrubbed CSV bytes accumulate here and
  // parts are sliced at EXACTLY PART_SIZE_BYTES, decoupled from row boundaries,
  // so every non-final part is byte-identical in size — the rule R2 (unlike S3)
  // enforces at complete(). A row may straddle two parts; R2 reassembles the
  // byte stream verbatim, so the object is unchanged.
  const chunker = new PartChunker(PART_SIZE_BYTES);

  // The single in-flight part upload (double-buffering): while part N's bytes
  // are on the wire, scrubbing continues into part N+1's buffer. At most ONE
  // part is ever in flight, so uploadPart calls stay strictly sequential
  // (multipart part numbers must be ordered) and memory stays bounded.
  let inflight: Promise<void> | null = null;

  /** Await the in-flight part upload (if any); its failure surfaces here. */
  const awaitInflight = async (): Promise<void> => {
    if (inflight === null) return;
    const previous = inflight;
    inflight = null;
    await previous;
  };

  /** Ship ONE part's bytes (double-buffered: waits for the previous part only). */
  const shipPart = async (partBytes: Uint8Array): Promise<void> => {
    const blob = new Blob([partBytes as unknown as BlobPart], {
      type: 'application/octet-stream',
    });
    const size = partBytes.byteLength;
    // Wait for the PREVIOUS part to land (keeps parts sequential + memory at
    // ~two parts), then start this part WITHOUT awaiting it — scrubbing the
    // next part overlaps this upload.
    await awaitInflight();
    const upload = transport.uploadPart(blob).then(() => {
      bytesUploaded += size;
      onProgress?.({ rowsProcessed, bytesUploaded });
    });
    // Mark a rejection handled on this side branch so a failure between now and
    // the next awaitInflight() can't fire an unhandled-rejection event; the
    // real error still surfaces at the next await.
    void upload.catch(() => {});
    inflight = upload;
  };

  /** Drain and ship every FULL (byte-exact) part currently available. */
  const shipFullParts = async (): Promise<void> => {
    for (const part of chunker.drainFullParts()) {
      await shipPart(part);
    }
  };

  const append = (text: string): void => {
    chunker.append(ENCODER.encode(text));
  };

  try {
    throwIfAborted();
    const stream = await openRowStream(file, plan.format);
    assertHeadersMatch(stream.headers, plan);

    const keptIndexes = scrubbedColumnIndexes(plan);

    // The designated columns' positions in the SCRUBBED OUTPUT (F1 index seam):
    // the index of the original-header column index within the kept-column order.
    // -1 (not kept / not designated) → null. This — not the name — is what the
    // ingest pins identity by; the name rides along as an equality assertion.
    const loanIdOutIndex =
      plan.loanIdColumnIndex === null ? -1 : keptIndexes.indexOf(plan.loanIdColumnIndex);
    const taxIdOutIndex =
      plan.taxIdColumnIndex === null ? -1 : keptIndexes.indexOf(plan.taxIdColumnIndex);

    await transport.begin({
      uploadId,
      fileType: plan.fileType,
      format: plan.format,
      declaredRows: 0,
      // Designate the Loan/Tax ID columns by scrubbed-output INDEX (authoritative)
      // plus the name they carry in the scrubbed header (HMAC'd in place, original
      // name kept) as defense-in-depth. Null when not designated.
      loanIdColumn: scrubbedNameForIndex(plan, plan.loanIdColumnIndex),
      taxIdColumn: scrubbedNameForIndex(plan, plan.taxIdColumnIndex),
      loanIdColumnIndex: loanIdOutIndex >= 0 ? loanIdOutIndex : null,
      taxIdColumnIndex: taxIdOutIndex >= 0 ? taxIdOutIndex : null,
    });
    began = true;

    // The scrubbed header row is the first thing in the stream.
    append(scrubbedHeaderRow(plan));

    let batch: ParsedRow[] = [];
    const flushBatch = async (): Promise<void> => {
      if (batch.length === 0) return;
      // Scrub the whole batch's rows concurrently (bounded by ROW_BATCH_SIZE),
      // keeping WebCrypto pipelined.
      const scrubbed = await Promise.all(
        batch.map((row) => scrubRowCells(row, plan, keptIndexes, cryptoKey)),
      );
      batch = [];
      for (const cells of scrubbed) {
        append(encodeCsvRow(cells));
        rowsProcessed += 1;
      }
      // Ship every full (byte-exact) part the batch produced; the remainder
      // stays buffered for the next batch (or the final short part).
      await shipFullParts();
      onProgress?.({ rowsProcessed, bytesUploaded });
    };

    for await (const row of stream.rows()) {
      throwIfAborted();
      batch.push(row);
      if (batch.length >= ROW_BATCH_SIZE) {
        await flushBatch();
      }
    }
    await flushBatch();

    // Ship the final (short) remainder part, wait for the last in-flight upload,
    // then finalize — carrying the source file's name + the accumulated
    // data-quality signals (P5) through to the persisted record.
    const remainder = chunker.drainRemainder();
    if (remainder !== null) {
      await shipPart(remainder);
    }
    await awaitInflight();
    throwIfAborted();
    const completed = await transport.complete(file.name, stream.dataQuality());
    return { ...completed, rowsProcessed, bytesUploaded };
  } catch (error) {
    // Tear down the transport on any failure so no orphaned multipart lingers.
    // Only meaningful once the session was opened; abort() is a no-op before
    // begin(). Swallow abort failures so the original error surfaces.
    if (began) {
      try {
        await transport.abort();
      } catch {
        // ignore — the original error is what the caller needs to see.
      }
    }
    throw error;
  }
}
